import Exim from '@/models/Exim.js'
import LandingPageHTMLSection from './LandingPageSections/LandingPageHTMLSection.js'
import LandingPagePrimaryQuoteSection from './LandingPageSections/LandingPagePrimaryQuoteSection.js'
import LandingPageImageSection from './LandingPageSections/LandingPageImageSection.js'
import LandingPageSliderSection from './LandingPageSections/LandingPageSliderSection.js'
import LandingPageStorefrontSection from './LandingPageSections/LandingPageStorefrontSection.js'
import LandingPageChatSection from './LandingPageSections/LandingPageChatSection.js'
import LandingPageBuiltInSection from './LandingPageSections/LandingPageBuiltInSection.js'



class LandingPage extends Exim {


	id = 0
	urlSlug = ''
	adminLabel = ''
	sections = []
	affiliates = null
	mastheadLinks = null   // null = use EcommHeader's default links; array of { label, href, target } = override
	headerMaxWidth = null  // null = use EcommHeader's default max-width; CSS length string (e.g. '1200px') = override


	clone() { return LandingPage.import( this.export() ) }
	isSame(obj) { return JSON.stringify( this.export() ) == JSON.stringify( obj.export() ) }


	/**
	 * A section's name is the join key between a template section and an affiliate's override -
	 * both in the admin editor (SectionManager, findAffiliateSection) and in the server-side
	 * merge (ecommerceAPI::getLandingPage, which matches on $item->name).  Duplicate names
	 * therefore break overrides silently, so every section added here gets a unique one.
	 *
	 * @param {string} baseName            name to use if it's free
	 * @param {object} excludeSection      section to ignore when checking (i.e. the one being renamed)
	 * @return {string} baseName, or 'baseName 2' / 'baseName 3' / ... if already taken
	 */
	uniqueSectionName( baseName, excludeSection = null ) {
		const taken = new Set()
		for( const section of this.sections ) {
			if( section === excludeSection ) continue
			taken.add( section.name || '' )
		}

		if( !taken.has( baseName ) ) return baseName

		let suffix = 2
		while( taken.has( `${baseName} ${suffix}` ) ) suffix++
		return `${baseName} ${suffix}`
	}

	addSection( index, section, baseName ) {
		section.name = this.uniqueSectionName( baseName )
		this.sections.splice( index, 0, section )
	}

	addHTMLSection( index ) { this.addSection( index, new LandingPageHTMLSection(), 'HTML Block' ) }
	addPrimaryQuoteSection( index ) { this.addSection( index, new LandingPagePrimaryQuoteSection(), 'Primary Quote' ) }
	addImageSection( index ) { this.addSection( index, new LandingPageImageSection(), 'Image' ) }
	addSliderSection( index ) { this.addSection( index, new LandingPageSliderSection(), 'Slider' ) }
	addStorefrontSection( index ) { this.addSection( index, new LandingPageStorefrontSection(), 'Storefront' ) }
	addChatSection( index ) { this.addSection( index, new LandingPageChatSection(), 'Tawk.to Chat' ) }
	addBuiltInSection( index ) { this.addSection( index, new LandingPageBuiltInSection(), 'Built-in Component' ) }


	/** Names used by more than one section (blank counts as a name) - these break affiliate overrides */
	duplicateSectionNames() {
		const seen = new Set()
		const duplicates = new Set()
		for( const section of this.sections ) {
			const name = section.name || ''
			if( seen.has( name ) ) duplicates.add( name )
			seen.add( name )
		}
		return duplicates
	}

	swapSections(idxa, idxb) {
		if(idxa < 0 || idxb < 0 || idxa >= this.sections.length || idxb >= this.sections.length) return;
		this.sections[idxa] = this.sections.splice(idxb, 1, this.sections[idxa])[0];
	}

	deleteSection(index) { this.sections.splice(index, 1); }


	// Masthead Links ( null = inherit client default; array = override )
	addMastheadLink( index = null ) {
		if( this.mastheadLinks === null ) this.mastheadLinks = [];
		const link = { label: '', href: '', target: null };
		if( index === null ) this.mastheadLinks.push( link );
		else this.mastheadLinks.splice( index, 0, link );
	}

	removeMastheadLink( index ) {
		if( this.mastheadLinks === null ) return;
		this.mastheadLinks.splice( index, 1 );
		if( this.mastheadLinks.length === 0 ) this.mastheadLinks = null;
	}

	swapMastheadLinks(idxa, idxb) {
		if( this.mastheadLinks === null ) return;
		if(idxa < 0 || idxb < 0 || idxa >= this.mastheadLinks.length || idxb >= this.mastheadLinks.length) return;
		this.mastheadLinks[idxa] = this.mastheadLinks.splice(idxb, 1, this.mastheadLinks[idxa])[0];
	}




	static import( obj ) {

		const lp = super.import( obj, [ 'sections' ] )

		// Sections
		if(obj.sections) for(let section of obj.sections) {
			let importedSection = null;
			if(!section.type || section.type == 'html') importedSection = LandingPageHTMLSection.import(section);
			else if(section.type == 'primaryQuote') importedSection = LandingPagePrimaryQuoteSection.import(section);
			else if(section.type == 'image') importedSection = LandingPageImageSection.import(section);
			else if(section.type == 'slider') importedSection = LandingPageSliderSection.import(section);
			else if(section.type == 'storefront') importedSection = LandingPageStorefrontSection.import(section);
			else if(section.type == 'chat') importedSection = LandingPageChatSection.import(section);
			else if(section.type == 'builtin') importedSection = LandingPageBuiltInSection.import(section);
			lp.sections.push( importedSection );
		}

		return lp;
	}


	export() {

		const data = super.export( [ 'sections' ] )

		data.sections = []
		for( let sec of this.sections ) data.sections.push( sec.export() )
		return data
	}



}

export default LandingPage;
